import { JobApplicationStatus, jobApplicationStatusLabels } from '@modules/jobApplications/statuses';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Checkbox, Divider, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { RadioButtonChecked } from '@mui/icons-material';

interface JobApplicationFilterStatusProps {
    value?: string[];
    onChange: (statuses: string[]) => void;
    id?: string;
}

const statusOptions = Object.values(JobApplicationStatus);

const statusIndicatorColorMap: Record<JobApplicationStatus, string> = {
    [JobApplicationStatus.New]: 'statusContact.main',
    [JobApplicationStatus.Interview]: 'statusInterview.main',
    [JobApplicationStatus.Trial]: 'statusTrial.main',
    [JobApplicationStatus.Offer]: 'statusProposal.main',
    [JobApplicationStatus.ProbationPeriod]: 'statusProbation.main',
    [JobApplicationStatus.Hired]: 'successGreen.main',
    [JobApplicationStatus.Rejected]: 'secondaryRed.main',
};

const JobApplicationFilterStatus: React.FC<JobApplicationFilterStatusProps> = ({
    value = [],
    onChange,
    id = 'jobApplicationStatus',
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isOpen = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getButtonLabel = (selectedStatuses: string[]) => {
        if (!selectedStatuses.length) {
            return 'Statut';
        }

        if (selectedStatuses.length === 1) {
            return (
                jobApplicationStatusLabels[selectedStatuses[0] as keyof typeof jobApplicationStatusLabels] ??
                selectedStatuses[0]
            );
        }

        return `${selectedStatuses.length} statuts sélectionnés`;
    };

    const menuId = useMemo(() => `${id}-menu`, [id]);

    const selectedStatuses = Array.isArray(value) ? value : [];
    const isAllSelected = selectedStatuses.length === statusOptions.length;

    const handleToggleStatus = (status: string) => {
        const exists = selectedStatuses.includes(status);
        const updatedStatuses = exists
            ? selectedStatuses.filter((selectedStatus: string) => selectedStatus !== status)
            : [...selectedStatuses, status];

        onChange(updatedStatuses);
    };

    const handleToggleAllStatuses = () => {
        onChange(isAllSelected ? [] : [...statusOptions]);
    };

    const renderStatusIndicator = (status: JobApplicationStatus) => {
        if (status === JobApplicationStatus.Hired) {
            return (
                <Box
                    sx={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        bgcolor: 'successGreen.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <CheckIcon sx={{ color: 'neutralWhite.main', fontSize: '12px' }} />
                </Box>
            );
        }

        if (status === JobApplicationStatus.Rejected) {
            return (
                <Box
                    sx={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        bgcolor: 'secondaryRed.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <CloseIcon sx={{ color: 'neutralWhite.main', fontSize: '12px' }} />
                </Box>
            );
        }

        return (
            <RadioButtonChecked
                sx={{
                    color: statusIndicatorColorMap[status],
                }}
            />
        );
    };

    const optionTextSx = {
        color: 'textSecondary.main',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '20px',
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    width: '100%',
                    minWidth: '134px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '24px',
                    background: theme => theme.palette.background.paper,
                    border: theme => `2px solid ${theme.palette.border.main}`,
                    color: theme => theme.palette.text.primary,
                    padding: '11px 14px',
                    '& .MuiButton-endIcon': {
                        marginLeft: '8px',
                    },
                    '& .MuiSvgIcon-root': {
                        color: theme => theme.palette.text.primary,
                    },
                    '&:hover': {
                        background: theme => theme.palette.background.paper,
                        border: theme => `2px solid ${theme.palette.border.main}`,
                    },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                    <Typography variant="body2">{getButtonLabel(selectedStatuses)}</Typography>
                </Box>
            </Button>

            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            width: '342px',
                            maxWidth: 'calc(100vw - 24px)',
                            borderRadius: '16px',
                            px: '16px',
                            py: '16px',
                            bgcolor: 'background.default',
                            border: 'none',
                            boxShadow: '0px 16px 40px 0px rgba(112, 144, 176, 0.2)',
                        },
                    },
                }}>
                <MenuItem
                    onClick={handleToggleAllStatuses}
                    sx={{
                        minHeight: '40px',
                        px: 0,
                        gap: '12px',
                    }}>
                    <Checkbox
                        checked={isAllSelected}
                        size="small"
                        sx={{
                            p: 0,
                        }}
                    />
                    <Typography sx={optionTextSx}>Sélectionner tout</Typography>
                    <Divider
                        sx={{
                            my: '8px',
                            borderStyle: 'dashed',
                            borderColor: 'textSecondary.main',
                            opacity: 0.6,
                        }}
                    />
                </MenuItem>

                {statusOptions.map(status => {
                    const checked = selectedStatuses.includes(status);
                    const label =
                        jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels] ?? status;

                    const statusKey = status as JobApplicationStatus;
                    const shouldShowDivider = statusKey === JobApplicationStatus.Hired;

                    return (
                        <React.Fragment key={status}>
                            {shouldShowDivider && (
                                <Divider
                                    sx={{
                                        my: '8px',
                                        borderStyle: 'dashed',
                                        borderColor: 'textSecondary.main',
                                        opacity: 0.6,
                                    }}
                                />
                            )}

                            <MenuItem
                                onClick={() => handleToggleStatus(status)}
                                sx={{
                                    minHeight: '40px',
                                    px: 0,
                                    gap: '12px',
                                }}>
                                <Checkbox
                                    checked={checked}
                                    size="small"
                                    sx={{
                                        p: 0,
                                    }}
                                />

                                {renderStatusIndicator(statusKey)}

                                <ListItemText
                                    primary={label}
                                    primaryTypographyProps={{
                                        sx: optionTextSx,
                                    }}
                                />
                            </MenuItem>
                        </React.Fragment>
                    );
                })}
            </Menu>
        </>
    );
};

export default JobApplicationFilterStatus;
